const LAYER_LABELS_BY_GRAPH = {
  grammar: {
    0: 'Foundation',
    1: 'Agreement',
    2: 'Structure',
    3: 'Modifiers & Punctuation',
    4: 'Rhetoric & Style',
  },
  math: {
    0: 'Number Sense & Operations',
    1: 'Algebraic Reasoning',
    2: 'Linear Functions & Data',
    3: 'Nonlinear Functions & Geometry',
    4: 'Advanced Modeling',
  },
}

function MasteryBadge({ level }) {
  if (level == null) return (
    <span className="text-xs px-2 py-0.5 rounded border border-dashed border-gray-400 dark:border-gray-600 text-gray-500 dark:text-gray-400">
      Not assessed
    </span>
  )

  const pct = Math.round(level * 100)
  let style = 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  if (pct >= 80) style = 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
  else if (pct >= 50) style = 'bg-gray-600 dark:bg-gray-400 text-white dark:text-gray-900'
  else if (pct >= 25) style = 'bg-gray-400 dark:bg-gray-500 text-gray-900 dark:text-gray-100'

  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${style}`}>
      {pct}% mastery
    </span>
  )
}

export default function ConceptDetailPanel({
  concept,
  concepts,
  edges,
  masteryData,
  graph = 'grammar',
  onClose,
  onTraceWeakness,
  onSelectConcept,
}) {
  if (!concept) return null

  const nodeId = concept.sat_node_id || concept.id
  const conceptMap = new Map(concepts.map(c => [c.sat_node_id || c.id, c]))
  const prerequisites = [...new Set(edges.filter(e => e.source === nodeId).map(e => e.target))]
  const dependents = [...new Set(edges.filter(e => e.target === nodeId).map(e => e.source))]
  const layer = concept.layer ?? '-'
  const layerLabels = LAYER_LABELS_BY_GRAPH[graph] || LAYER_LABELS_BY_GRAPH.grammar
  const mastery = masteryData?.[nodeId]
  const isWeak = mastery && mastery.mastery_level < 0.5
  const showTraceButton = masteryData && (isWeak || !mastery) && prerequisites.length > 0

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4 space-y-3 sticky top-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight">
            {concept.subcategory || concept.guideword || concept.id}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
            {nodeId}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-lg leading-none shrink-0"
        >
          &times;
        </button>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 text-xs">
        {layer !== '-' && (
          <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
            L{layer} {layerLabels[layer] || ''}
          </span>
        )}
        {concept.category && (
          <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
            {concept.category}
          </span>
        )}
        <span className={`px-2 py-0.5 rounded border ${
          concept.sat_tested === false
            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
            : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
        }`}>
          {concept.sat_tested === false ? 'Foundational' : 'SAT'}
        </span>
      </div>

      {/* Mastery */}
      {masteryData && (
        <div>
          <MasteryBadge level={mastery?.mastery_level} />
          {mastery?.attempts > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
              {mastery.correct}/{mastery.attempts} correct
            </span>
          )}
        </div>
      )}

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        {concept.description}
      </p>


      {/* Prerequisites */}
      {prerequisites.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Prerequisites ({prerequisites.length})
          </h4>
          <div className="flex flex-wrap gap-1">
            {prerequisites.map(id => {
              const c = conceptMap.get(id)
              const label = c ? (c.subcategory || c.guideword || id) : id
              return (
                <button
                  key={id}
                  onClick={() => onSelectConcept?.(id)}
                  className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Dependents (Unlocks) */}
      {dependents.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Unlocks ({dependents.length})
          </h4>
          <div className="flex flex-wrap gap-1">
            {dependents.map(id => {
              const c = conceptMap.get(id)
              const label = c ? (c.subcategory || c.guideword || id) : id
              return (
                <button
                  key={id}
                  onClick={() => onSelectConcept?.(id)}
                  className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Trace weakness button */}
      {showTraceButton && (
        <button
          onClick={() => onTraceWeakness?.(nodeId)}
          className="w-full mt-2 px-3 py-2 text-xs font-medium bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
        >
          Trace root weakness
        </button>
      )}
    </div>
  )
}
